// Studio Tor 0 (05.10.2026): ein von Hand montiertes Scrolly, ohne KI.
//
// Frage dahinter: Ist eine sorgfaeltige Montage aus den Bausteinen, die der
// Renderer heute kann (Jans Infografik, MapTiler-Kartenfahrt, Kamera-Naehte),
// ein Wunder? Wenn nicht, macht es auch keine KI dazu. Das Drehbuch hier
// (Reihenfolge, Hervorhebung, Uebergaenge, Texte) ist genau der Teil, den
// spaeter die Maschine entscheiden soll.
//
// Daten: Destatis, „Binnenwanderungen 2025 nach Bundeslaendern“, Stand
// 01.06.2026, abgerufen 05.10.2026, Ziffer fuer Ziffer gegen das HTML geprueft.
// Keine Stroeme Land→Land: die Tabelle nennt nur Salden, also behauptet der
// Text auch nur Salden.
//
// Lokal: /s/studio-tor0 (Mock-Fallback, kein Backend noetig). Die Kartenfahrt
// braucht VITE_MAPTILER_KEY in public/.env.local, ohne Schluessel bleibt sie leer.
import type { PostJson } from "@scrolly/post-types";

// ── Daten ──────────────────────────────────────────────────────────────────
const QUELLE =
  "Quelle: Statistisches Bundesamt (Destatis), Binnenwanderungen 2025 nach Bundesländern, Stand 01.06.2026";

const LAENDER: { land: string; saldo: number; je10000: number }[] = [
  { land: "Baden-Württemberg", saldo: -3829, je10000: -3.41 },
  { land: "Bayern", saldo: 8439, je10000: 6.39 },
  { land: "Berlin", saldo: -12486, je10000: -33.99 },
  { land: "Brandenburg", saldo: 9044, je10000: 35.39 },
  { land: "Bremen", saldo: -1734, je10000: -24.64 },
  { land: "Hamburg", saldo: -1895, je10000: -10.2 },
  { land: "Hessen", saldo: -4118, je10000: -6.56 },
  { land: "Mecklenburg-Vorpommern", saldo: 3184, je10000: 20.21 },
  { land: "Niedersachsen", saldo: 6069, je10000: 7.58 },
  { land: "Nordrhein-Westfalen", saldo: -4662, je10000: -2.59 },
  { land: "Rheinland-Pfalz", saldo: 1340, je10000: 3.25 },
  { land: "Saarland", saldo: -588, je10000: -5.8 },
  { land: "Sachsen", saldo: -496, je10000: -1.23 },
  { land: "Sachsen-Anhalt", saldo: -599, je10000: -2.8 },
  { land: "Schleswig-Holstein", saldo: 8376, je10000: 28.33 },
  { land: "Thüringen", saldo: -6051, je10000: -28.71 },
];
const WANDERUNGEN_GESAMT = "996.216";

// ── Gestaltung (verriegelt, nicht generiert) ───────────────────────────────
const PAPIER = { r: 250, g: 247, b: 242, a: 1 };
const TINTE = "#2A2320";
const GEWINN = "#F02873";
const GEWINN_LEISE = "#F7A3C0";
const VERLUST = "#2F6690";
const VERLUST_LEISE = "#9DB8D0";
const DIVERGENT = ["#2F6690", "#9DB8D0", "#EDE8DF", "#F7A3C0", "#F02873"];
const SCHRIFT = "BarlowSemiCondensed";

// Infografik im Hochformat, auf die sichtbare Buehnenbreite skaliert
// (gleiche Regel wie infographicFillScale in src/studio/montage/assemble.ts).
const BREITE = 1080;
const HOEHE = 1700;
const FUELL = 2304 / BREITE;
// Karten sind breiter als hoch und liefen rechts aus dem Bild: etwas Rand lassen.
const fuell = (inhalt: Record<string, unknown>) =>
  String(inhalt.chartType).startsWith("map") ? FUELL * 0.88 : FUELL;

// ── Bausteine ──────────────────────────────────────────────────────────────
type Kf = { id: string; start: number; value: number | number[]; easing: string };
const kf = (id: string, start: number, value: number | number[], easing = "quadInOut"): Kf => ({
  id,
  start,
  value,
  easing,
});
const kanal = (id: string, type: string, initialValue: unknown, keyframes: Kf[] = []) => ({
  id,
  type,
  active: keyframes.length > 0,
  keyframes,
  initialValue,
});
const ruhig = (p: string) => ({
  scale: kanal(`${p}-s`, "scale", [1, 1, 1]),
  opacity: kanal(`${p}-o`, "opacity", 1),
  position: kanal(`${p}-p`, "position", [0, 0, 0]),
  rotation: kanal(`${p}-r`, "rotation", [0, 0, 0]),
});

type Grafik = Record<string, unknown> & { chartType: string; title: string };
const grafik = (g: Grafik) => ({
  layerIds: [],
  name: g.title,
  asHtml: true,
  width: BREITE,
  height: HOEHE,
  source: QUELLE.replace("Quelle: ", ""),
  palette: [GEWINN, VERLUST],
  textColor: TINTE,
  backgroundColor: PAPIER,
  accentColor: GEWINN,
  fontFamily: SCHRIFT,
  titleFontSize: 64,
  sourceFontSize: 22,
  thousandsSep: "dot",
  countUp: true,
  showValues: true,
  showLabels: true,
  ...g,
});

const nachWert = <T extends { saldo: number }>(xs: T[], dir: 1 | -1) =>
  [...xs].sort((a, b) => dir * (b.saldo - a.saldo));
const gewinner = nachWert(LAENDER.filter((l) => l.saldo > 0), 1);
const verlierer = nachWert(LAENDER.filter((l) => l.saldo < 0), -1);
// Gleiche Achse fuer beide Balken-Beats: die Laengen sind vergleichbar.
const ACHSE = 15000; // Luft fuer die Wertbeschriftung neben dem laengsten Balken

// ── Drehbuch ───────────────────────────────────────────────────────────────
// naht: wie dieser Beat in den naechsten uebergeht.
//   "rueck"    die Szene weicht zurueck (wird klein), die naechste waechst heran
//   "durch"    die Kamera fliegt durch die Szene (wird gross), die naechste kommt aus der Tiefe
//   "schieben" die Szene gleitet nach links hinaus, die naechste von rechts herein
//   "blende"   ruhige Ueberblendung
// kamera: Fahrten innerhalb einer Grafik. x/y = Bildschirm-Versatz des Ziels
// zur Bildmitte in Pixeln bei 390 px Breite (am Filmstrip abgemessen), zoom = Vergroesserung.
type Naht = "rueck" | "durch" | "schieben" | "blende";
type Kamera = { zoom: number; x: number; y: number; text?: string };
type Station = {
  lng: number;
  lat: number;
  zoom: number;
  pitch?: number;
  bearing?: number;
  text?: string;
};
type Beat =
  | { art: "grafik"; id: string; text: string; naht: Naht; inhalt: Record<string, unknown>; kamera?: Kamera[] }
  | { art: "karte"; id: string; naht: Naht; stationen: Station[] };

const BEATS: Beat[] = [
  {
    art: "karte",
    id: "anflug",
    naht: "durch",
    stationen: [
      { lng: 12, lat: 50, zoom: 2.6 },
      {
        lng: 10.45,
        lat: 51.1,
        zoom: 5.1,
        pitch: 40,
        bearing: -8,
        text: "Deutschland 2025: Wer zieht von wo nach wo?",
      },
    ],
  },
  {
    art: "grafik",
    id: "zahl",
    naht: "rueck",
    text: "So oft zogen Menschen 2025 von einem Bundesland in ein anderes. Wohin?",
    inhalt: grafik({
      chartType: "kpi",
      title: "",
      value: WANDERUNGEN_GESAMT,
      unit: "Umzüge über Ländergrenzen",
      data: [],
      valueFontSize: 210,
      labelFontSize: 44,
    }),
  },
  {
    art: "grafik",
    id: "karte-absolut",
    naht: "blende",
    text: "Sechs Länder gewinnen Einwohner dazu, zehn verlieren.",
    kamera: [
      { zoom: 2.6, x: 141, y: -76, text: "Berlin verliert, Brandenburg rundherum gewinnt." },
      {
        zoom: 2.2,
        x: -40,
        y: -150,
        text: "Im Norden dasselbe Muster: Hamburg und Bremen verlieren, Schleswig-Holstein und Niedersachsen gewinnen.",
      },
      { zoom: 1, x: 0, y: 0 },
    ],
    inhalt: grafik({
      chartType: "mapCountry",
      mapCountry: "DEU",
      mapResolution: "50m",
      title: "Wanderungssaldo 2025",
      data: LAENDER.map((l) => ({ label: l.land, value: l.saldo })),
      mapColorMode: "value",
      mapColorRamp: "custom",
      mapColorRampCustom: DIVERGENT,
      mapThresholds: [-10000, -5000, -1000, 0, 1000, 5000, 10000],
      mapLegend: true,
      mapLegendFontSize: 24,
      mapStrokeColor: "#FFFFFF",
      mapStrokeWidth: 1.5,
      showValues: false,
      showLabels: false,
    }),
  },
  {
    art: "grafik",
    id: "gewinner",
    naht: "schieben",
    text: "Brandenburg gewinnt am meisten, knapp vor Bayern und Schleswig-Holstein.",
    inhalt: grafik({
      chartType: "bar",
      horizontal: true,
      title: "Wer gewinnt",
      data: gewinner.map((l, i) => ({
        label: l.land,
        value: l.saldo,
        color: i === 0 ? GEWINN : GEWINN_LEISE,
      })),
      valuePrefix: "+",
      axisMax: ACHSE,
      barWidthPct: 62,
      barRadius: 6,
      labelFontSize: 34,
      valueFontSize: 34,
    }),
  },
  {
    art: "grafik",
    id: "verlierer",
    naht: "rueck",
    text: "Berlin verliert am stärksten: 12.486 Menschen mehr zogen weg als zu.",
    inhalt: grafik({
      chartType: "bar",
      horizontal: true,
      title: "Wer verliert",
      data: verlierer.map((l, i) => ({
        label: l.land,
        value: -l.saldo,
        color: i === 0 ? VERLUST : VERLUST_LEISE,
      })),
      valuePrefix: "−",
      axisMax: ACHSE,
      barWidthPct: 62,
      barRadius: 6,
      labelFontSize: 30,
      valueFontSize: 30,
    }),
  },
  {
    art: "grafik",
    id: "karte-je-kopf",
    naht: "blende",
    text: "Pro Kopf gerechnet kippt das Bild: Thüringen verliert fast so stark wie Berlin.",
    inhalt: grafik({
      chartType: "mapCountry",
      mapCountry: "DEU",
      mapResolution: "50m",
      title: "Saldo je 10.000 Einwohner",
      data: LAENDER.map((l) => ({ label: l.land, value: l.je10000 })),
      mapColorMode: "value",
      mapColorRamp: "custom",
      mapColorRampCustom: DIVERGENT,
      mapThresholds: [-30, -20, -5, 0, 5, 20, 30],
      mapLegend: true,
      mapLegendFontSize: 24,
      mapStrokeColor: "#FFFFFF",
      mapStrokeWidth: 1.5,
      showValues: false,
      showLabels: false,
      countUp: false,
    }),
  },
];

// ── Montage (Zeitachse) ────────────────────────────────────────────────────
// Regeln aus der Naht-Golden-Referenz und assemble.ts:
// - Lebenszeit einer Szene liegt auf ihren Ebenen, nicht auf der Gruppe.
// - Opacity nie auf exakt 0, sondern 0.001.
// - Jeder Beat startet im Ausgangszustand (Fortschritt 0), nie fertig.
// - Der Zeitgeber laeuft linear zwischen den Slides; ohne zweite Slide steht er.
const ANKER = 1000; // slides[0], leer; hier steht der Zeitgeber nach START
const AUFBAU = 1600; // Dauer Fortschritt 0 → 1
const FAHRT = 1800; // Dauer einer Kamerafahrt / Kartenstation
const HALTEN = 700; // nach dem letzten Moment, bevor die Naht beginnt
const NAHT = 2400; // Dauer einer Naht
// Positionen von Grafik-Ebenen sind normiert: 1.0 = halbe Containerbreite (X)
// bzw. halbe Containerhoehe (Y), siehe renderLayerHTML in RendererWebGL.ts.
// Die Kamera-Ziele sind am Handy-Filmstrip (390 x 844 px) abgemessen.
const W = 390;
const H = 844;
const RAUS = 2.1; // ganz aus dem Bild geschoben

function montiere(beats: Beat[]) {
  const layers: Record<string, unknown>[] = [];
  const szenen: Record<string, unknown>[] = [];
  const slides: Record<string, unknown>[] = [
    {
      id: "slide-0",
      from: ANKER,
      name: "Start",
      text: { show: false, text: "" },
      credits: { text: "", light: false },
      options: null,
      isChapter: true,
    },
  ];
  const frames: { textSlideIds: string[]; textLayerIds: string[]; imageLayerIds: string[] }[] = [];
  const textSlide = (id: string, from: number, text: string, credits = "", chapter = false) => {
    slides.push({
      id,
      from,
      name: id,
      text: { show: true, text },
      credits: { text: credits, light: false },
      options: null,
      isChapter: chapter,
    });
    frames.push({ textSlideIds: [id], textLayerIds: [], imageLayerIds: [] });
  };

  let einStart = 0;
  let einEnde = ANKER; // erster Beat: ganz da, wenn der Leser START drueckt
  let vorigeNaht: Naht | null = null;

  beats.forEach((b, i) => {
    const istLetzter = i === beats.length - 1;
    const layerId = `ebene-${b.id}`;
    const pre = `szene-${b.id}`;
    // Der Aufbau beginnt erst, wenn der Beat ganz da ist (Ausgangszustand sichtbar).
    const t0 = Math.max(einEnde, ANKER) + 200;
    const camScale: Kf[] = [];
    const camPos: Kf[] = [];
    let ende: number;

    if (b.art === "grafik") {
      const fertig = t0 + AUFBAU;
      textSlide(`slide-${b.id}`, fertig + 200, b.text, "", true);
      ende = fertig + 200;
      if (b.kamera?.length) {
        camScale.push(kf(`${pre}-cs0`, ende + 300, [1, 1, 1]));
        camPos.push(kf(`${pre}-cp0`, ende + 300, [0, 0, 0]));
        b.kamera.forEach((k, j) => {
          const t = ende + 300 + FAHRT;
          camScale.push(kf(`${pre}-cs${j + 1}`, t, [k.zoom, k.zoom, k.zoom]));
          camPos.push(kf(`${pre}-cp${j + 1}`, t, [(-2 * k.x * k.zoom) / W, (2 * k.y * k.zoom) / H, 0]));
          if (k.text) textSlide(`slide-${b.id}-k${j + 1}`, t + 200, k.text);
          ende = t + 200;
        });
      }
      layers.push({
        id: layerId,
        from: Math.max(einStart - 100, 0),
        to: 0, // unten gesetzt
        name: (b.inhalt.title as string) || b.id,
        type: "infographic",
        visible: true,
        pivotPoint: [0, 0, 0],
        content: b.inhalt,
        animationsGroups: {
          ...ruhig(layerId),
          scale: kanal(`${layerId}-s`, "scale", [fuell(b.inhalt), fuell(b.inhalt), 1]),
          progress: kanal(`${layerId}-prog`, "progress", 0, [
            kf(`${layerId}-p0`, t0, 0),
            kf(`${layerId}-p1`, fertig, 1),
          ]),
        },
      });
    } else {
      // Kartenfahrt: Station 0 steht beim START, jede weitere ist eine Fahrt.
      const zeiten = b.stationen.map((_, k) => (k === 0 ? 0 : t0 + k * FAHRT));
      b.stationen.forEach((s, k) => {
        if (s.text) textSlide(`slide-${b.id}-${k}`, zeiten[k] + 200, s.text, "Karte: © MapTiler © OpenStreetMap-Mitwirkende", k === 1);
      });
      ende = zeiten[zeiten.length - 1] + 200;
      layers.push({
        id: layerId,
        from: Math.max(einStart - 100, 0),
        to: 0,
        name: "Kartenfahrt",
        type: "map",
        visible: true,
        pivotPoint: [0, 0, 0],
        content: {
          style: "dataviz",
          interactive: false,
          apiKey: import.meta.env.VITE_MAPTILER_KEY ?? "",
          waypoints: b.stationen.map((s, k) => ({
            scrollPosition: k === 0 ? t0 : zeiten[k],
            lng: s.lng,
            lat: s.lat,
            zoom: s.zoom,
            bearing: s.bearing ?? 0,
            pitch: s.pitch ?? 0,
            easing: "quadInOut",
            markers: [],
          })),
        },
        animationsGroups: ruhig(layerId),
      });
    }

    const nahtStart = ende + HALTEN;
    const nahtEnde = nahtStart + NAHT;
    const ebeneBis = istLetzter ? ende + 3000 : nahtEnde + 100;
    (layers[layers.length - 1] as { to: number }).to = ebeneBis;

    // Szene: Einblendung (vorige Naht) · Kamera · Ausblendung (eigene Naht)
    const scaleKfs: Kf[] = [];
    const opacityKfs: Kf[] = [];
    const posKfs: Kf[] = [];
    if (i > 0) {
      const von = vorigeNaht === "durch" ? 0.6 : vorigeNaht === "rueck" ? 0.32 : 0.98;
      scaleKfs.push(kf(`${pre}-s-in0`, einStart, [von, von, von], "quadOut"));
      scaleKfs.push(kf(`${pre}-s-in1`, einEnde, [1, 1, 1], "quadOut"));
      opacityKfs.push(kf(`${pre}-o-in0`, einStart, vorigeNaht === "schieben" ? 1 : 0.001, "quadOut"));
      opacityKfs.push(kf(`${pre}-o-in1`, einEnde, 1, "quadOut"));
      if (vorigeNaht === "schieben") {
        posKfs.push(kf(`${pre}-p-in0`, einStart, [RAUS, 0, 0], "quadInOut"));
        posKfs.push(kf(`${pre}-p-in1`, einEnde, [0, 0, 0], "quadInOut"));
      }
    }
    scaleKfs.push(...camScale);
    posKfs.push(...camPos);
    if (!istLetzter) {
      const nach = b.naht === "durch" ? 2.4 : b.naht === "rueck" ? 0.32 : b.naht === "schieben" ? 1 : 1.02;
      scaleKfs.push(kf(`${pre}-s-out0`, nahtStart, [1, 1, 1], "quadIn"));
      scaleKfs.push(kf(`${pre}-s-out1`, nahtEnde, [nach, nach, nach], "quadIn"));
      if (b.naht === "schieben") {
        posKfs.push(kf(`${pre}-p-out0`, nahtStart, [0, 0, 0], "quadInOut"));
        posKfs.push(kf(`${pre}-p-out1`, nahtEnde, [-RAUS, 0, 0], "quadInOut"));
      } else {
        opacityKfs.push(kf(`${pre}-o-out0`, nahtStart, 1, "quadIn"));
        opacityKfs.push(kf(`${pre}-o-out1`, nahtStart + NAHT * (b.naht === "durch" ? 0.6 : 0.85), 0.001, "quadIn"));
      }
    }
    const erst = (ks: Kf[], fallback: unknown) => (ks.length ? ks[0].value : fallback);
    szenen.push({
      id: pre,
      from: Math.max(einStart - 100, 0),
      to: ebeneBis,
      name: `Beat ${i + 1}: ${b.id}`,
      type: "group",
      visible: true,
      pivotPoint: [0, 0, 0],
      content: { layerIds: [layerId] },
      animationsGroups: {
        ...ruhig(pre),
        scale: kanal(`${pre}-s`, "scale", erst(scaleKfs, [1, 1, 1]), scaleKfs),
        opacity: kanal(`${pre}-o`, "opacity", i === 0 ? 1 : erst(opacityKfs, 1), opacityKfs),
        position: kanal(`${pre}-p`, "position", erst(posKfs, [0, 0, 0]), posKfs),
      },
    });

    // Naechster Beat kommt, waehrend dieser geht.
    einStart = nahtStart + (b.naht === "schieben" ? 0 : 300);
    einEnde = nahtEnde + (b.naht === "schieben" ? 0 : 200);
    vorigeNaht = b.naht;
  });

  // Schluss: Quelle.
  const schlussVon = (slides[slides.length - 1].from as number) + 1600;
  textSlide("slide-schluss", schlussVon, QUELLE, "", true);

  const stage = {
    id: "layer_group_1",
    from: 0,
    to: schlussVon + 2000,
    name: "Stage",
    type: "group",
    visible: true,
    pivotPoint: [0, 0, 0],
    content: { layerIds: szenen.map((s) => s.id) },
    animationsGroups: ruhig("stage"),
  };

  return { groups: [stage, ...szenen], layers, slides, frames };
}

const M = montiere(BEATS);

export const mockPostJsonStudioTor0: PostJson = {
  data: {
    id: "dev-seed-studio-tor0",
    slug: "studio-tor0",
    title: "Wohin ziehen die Deutschen?",
    authors: [],
    tenants: ["scrlly"],
    visibility: "public",
    publishState: "published",
    metadata: {
      type: "visual",
      theme: "light",
      title: "Wohin ziehen die Deutschen?",
      subtitle: "Umzüge zwischen den Bundesländern 2025",
      description: "Studio Tor 0: von Hand montiert, Daten von Destatis.",
      hidden: false,
      hideIntroOverlay: false,
    },
    templateStructure: { frames: M.frames },
    data: {
      id: "new",
      groups: M.groups,
      layers: M.layers,
      slides: M.slides,
      presentationOptions: {
        responsive: true,
        ambientIntensity: 1,
        sceneBackgroundColor: PAPIER,
        slideOptions: {
          backgroundColor: { r: 42, g: 35, b: 32, a: 0.92 },
          fontColor: { r: 250, g: 247, b: 242, a: 1 },
          margin: 12,
          padding: 16,
          scrollable: false,
          borderRadius: 12,
          animationType: "opacity",
          alignAtSafeArea: true,
          companyBoxPosition: "left",
          safeAreaXPosition: 0,
          safeAreaYPosition: 0,
        },
      },
    },
  },
} as unknown as PostJson;
